'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { SectionHeader, Grad } from '@/components/ui/Section';

interface ProcessStep {
  id: string;
  number: string;
  title: string;
  description: string;
  details: string[];
  icon: string;
}

const IntegrationProcess = () => {
  const [expandedStep, setExpandedStep] = useState<string | null>('step_consultation');

  const processSteps: ProcessStep[] = [
    {
      id: 'step_consultation',
      number: '01',
      title: 'Initial Consultation',
      description: 'Assess your material requirements and performance goals',
      details: [
        'Review current material formulations',
        'Identify performance bottlenecks',
        'Define target improvements',
        'Discuss regulatory requirements',
        'Establish project timeline',
      ],
      icon: 'ChatBubbleLeftRightIcon',
    },
    {
      id: 'step_testing',
      number: '02',
      title: 'Material Testing & Analysis',
      description: 'Laboratory evaluation of graphene integration',
      details: [
        'Baseline material characterization',
        'Graphene dispersion testing',
        'Optimal dosage determination',
        'Performance validation',
        'Quality control protocols',
      ],
      icon: 'BeakerIcon',
    },
    {
      id: 'step_formulation',
      number: '03',
      title: 'Custom Formulation',
      description: 'Develop tailored graphene additive solution',
      details: [
        'Surface treatment selection',
        'Particle size optimization',
        'Compatibility testing',
        'Batch consistency verification',
        'Scale-up feasibility study',
      ],
      icon: 'AdjustmentsHorizontalIcon',
    },
    {
      id: 'step_integration',
      number: '04',
      title: 'Process Integration',
      description: 'Implement graphene additives into your production',
      details: [
        'Equipment compatibility assessment',
        'Mixing protocol development',
        'Process parameter optimization',
        'Quality control integration',
        'Operator training program',
      ],
      icon: 'CogIcon',
    },
    {
      id: 'step_validation',
      number: '05',
      title: 'Performance Validation',
      description: 'Verify enhanced material properties and consistency',
      details: [
        'Full-scale production trials',
        'Performance testing vs. targets',
        'Statistical process control',
        'Long-term stability evaluation',
        'Documentation and certification',
      ],
      icon: 'CheckBadgeIcon',
    },
    {
      id: 'step_support',
      number: '06',
      title: 'Ongoing Support',
      description: 'Continuous technical assistance and optimization',
      details: [
        'Regular performance monitoring',
        'Process optimization recommendations',
        'Troubleshooting support',
        'New application development',
        'Technical documentation updates',
      ],
      icon: 'LifebuoyIcon',
    },
  ];

  return (
    <section className="section relative overflow-hidden bg-ink text-white">
      {/* Same restrained canvas treatment as the other dark bands. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60rem_28rem_at_50%_-10%,rgba(14,165,233,0.09),transparent_60%)]"
      />

      <div className="container-page relative">
        {/* Header — existing copy, standardised presentation. */}
        <SectionHeader
          title={
            <>
              <Grad>Integration</Grad> Process
            </>
          }
          eyebrow="Implementation Guide"
          lead="Our proven six-step process ensures seamless integration of graphene additives into your production"
        />

        {/* Connected accordion */}
        <div className="relative mx-auto mt-11 max-w-4xl rounded-xl border border-white/10 bg-[#0a1322]/85 p-2.5 shadow-[0_24px_60px_rgba(2,8,23,0.5)]">
          {/* Progress line behind the icon column. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-12 top-12 left-[52px] w-px bg-gradient-to-b from-accent/60 via-accent/25 to-accent/10"
          />

          <div className="relative space-y-1.5">
            {processSteps.map((step) => {
              const isOpen = expandedStep === step.id;
              return (
                <div
                  key={step.id}
                  className={`rounded-lg transition-[background-color,box-shadow] duration-base ease-out ${
                    isOpen
                      ? 'bg-gradient-to-b from-[#0d1c36]/80 to-[#0a1526]/50 ring-1 ring-accent/40 shadow-[0_0_24px_rgba(14,165,233,0.1)]'
                      : ''
                  }`}
                >
                  <button
                    type="button"
                    id={`step-btn-${step.id}`}
                    aria-expanded={isOpen}
                    aria-controls={`step-panel-${step.id}`}
                    onClick={() => setExpandedStep(isOpen ? null : step.id)}
                    className={`group grid w-full grid-cols-[2.75rem_minmax(0,1fr)_auto] items-center gap-x-4 rounded-lg px-5 py-4 text-left transition-colors duration-fast ease-out sm:py-[1.125rem] ${
                      isOpen ? '' : 'hover:bg-white/[0.03]'
                    }`}
                  >
                    {/* Icon — solid surface so the line passes behind it. */}
                    <span
                      className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-lg border transition-[border-color,background-color,box-shadow] duration-base sm:h-10 sm:w-10 ${
                        isOpen
                          ? 'bg-accent-rich border-sky-400/60 text-white shadow-[0_4px_16px_rgba(14,165,233,0.35)]'
                          : 'border-accent/35 bg-[#0a1322] text-accent group-hover:border-accent/60'
                      }`}
                    >
                      <Icon name={step.icon as any} size={19} />
                    </span>

                    <span className="min-w-0">
                      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                        <span
                          className={`t-value text-xs font-bold tracking-[0.1em] ${
                            isOpen ? 'text-sky-300' : 'text-accent'
                          }`}
                        >
                          {step.number}
                        </span>
                        <span className="text-[0.9375rem] font-semibold leading-snug text-white sm:text-base">
                          {step.title}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-sm leading-snug text-slate-400">
                        {step.description}
                      </span>
                    </span>

                    <Icon
                      name="ChevronDownIcon"
                      size={20}
                      className={`shrink-0 text-slate-400 transition-transform duration-base ease-out group-hover:text-white motion-reduce:transition-none ${
                        isOpen ? 'rotate-180 text-sky-300' : ''
                      }`}
                    />
                  </button>

                  {/* Details — always mounted; grid-rows trick animates height and
                      opacity together with zero layout measurement. */}
                  <div
                    id={`step-panel-${step.id}`}
                    role="region"
                    aria-labelledby={`step-btn-${step.id}`}
                    className={`grid ${
                      isOpen
                        ? 'acc-panel acc-panel-open grid-rows-[1fr] opacity-100'
                        : 'acc-panel grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div
                        className={`transition-transform duration-slow ease-out ${
                          isOpen ? 'translate-y-0' : '-translate-y-1.5'
                        }`}
                      >
                        <div className="px-5 pb-5">
                          <div className="ml-[3.75rem] border-t border-white/10 pt-4">
                            <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-accent">
                              Key Activities
                            </h4>
                            <ul className="mt-3.5 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
                              {step.details.map((detail, detailIndex) => (
                                <li
                                  key={`detail_${step.id}_${detailIndex}`}
                                  className="flex items-start gap-2.5 text-sm text-slate-300"
                                >
                                  <Icon
                                    name="CheckCircleIcon"
                                    size={17}
                                    className="mt-0.5 shrink-0 text-accent"
                                  />
                                  <span className="min-w-0">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationProcess;
